import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Clock, Users, Trash2, Copy, RefreshCw } from 'lucide-react';
import api from '../services/api';

// Bulk Exclude/Include Teachers Dialog
export const BulkExcludeDialog = ({ open, onOpenChange, selectedTeachers, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [exclude, setExclude] = useState(true);

  const handleSubmit = async () => {
    if (selectedTeachers.length === 0) {
      toast.error('No teachers selected');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/bulk/teachers/exclude', {
        teacher_ids: selectedTeachers,
        exclude: exclude
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update teachers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Exclude/Include Teachers</DialogTitle>
          <DialogDescription>
            {selectedTeachers.length} teacher(s) selected
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center gap-4">
            <Button
              variant={exclude ? 'default' : 'outline'}
              onClick={() => setExclude(true)}
              className={exclude ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              Exclude from Conferences
            </Button>
            <Button
              variant={!exclude ? 'default' : 'outline'}
              onClick={() => setExclude(false)}
              className={!exclude ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              Include in Conferences
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {exclude 
              ? 'Excluded teachers will not appear when creating conference sets.'
              : 'Teachers will be available for selection in conference sets.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {exclude ? 'Exclude Teachers' : 'Include Teachers'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Time Adjustment Dialog
export const BulkTimeAdjustDialog = ({ open, onOpenChange, scheduleSetId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [minutes, setMinutes] = useState(15);
  const [direction, setDirection] = useState('forward');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const offset = direction === 'forward' ? minutes : -minutes;
      const response = await api.post('/bulk/schedule-sets/adjust-times', {
        schedule_set_id: scheduleSetId,
        minutes_offset: offset
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust times');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Bulk Time Adjustment
          </DialogTitle>
          <DialogDescription>
            Shift all time slots forward or backward
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Minutes to Shift</Label>
            <Select value={String(minutes)} onValueChange={(v) => setMinutes(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 minutes</SelectItem>
                <SelectItem value="10">10 minutes</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Direction</Label>
            <div className="flex gap-2">
              <Button
                variant={direction === 'forward' ? 'default' : 'outline'}
                onClick={() => setDirection('forward')}
                className="flex-1"
              >
                Forward (Later)
              </Button>
              <Button
                variant={direction === 'backward' ? 'default' : 'outline'}
                onClick={() => setDirection('backward')}
                className="flex-1"
              >
                Backward (Earlier)
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Adjust Times
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Attendance Change Dialog
export const BulkAttendanceDialog = ({ open, onOpenChange, exportSetId, selectedSlots, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState('Remote Only');
  const [applyToAll, setApplyToAll] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.post('/bulk/export-sets/attendance', {
        export_set_id: exportSetId,
        attendance_option: attendance,
        slot_ids: applyToAll ? null : selectedSlots
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Change Attendance</DialogTitle>
          <DialogDescription>
            {applyToAll ? 'Apply to all slots' : `${selectedSlots?.length || 0} slot(s) selected`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Attendance Option</Label>
            <Select value={attendance} onValueChange={setAttendance}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Remote Only">Remote Only</SelectItem>
                <SelectItem value="In-person Only">In-person Only</SelectItem>
                <SelectItem value="Attendee Choice">Attendee Choice</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox 
              id="apply-all" 
              checked={applyToAll} 
              onCheckedChange={setApplyToAll}
            />
            <Label htmlFor="apply-all" className="cursor-pointer">
              Apply to all slots in this export set
            </Label>
          </div>
          <p className="text-sm text-slate-500">
            Note: Teachers without rooms assigned will skip "In-person Only" and "Attendee Choice" options.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Update Attendance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Delete Slots Dialog
export const BulkDeleteSlotsDialog = ({ open, onOpenChange, exportSetId, slots, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('date');
  const [selectedDate, setSelectedDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);

  // Get unique dates from slots
  const uniqueDates = [...new Set(slots?.map(s => s.conference_date) || [])];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const request = {
        export_set_id: exportSetId,
        filter_type: filterType
      };

      if (filterType === 'date') {
        request.date = selectedDate;
      } else if (filterType === 'time_range') {
        request.start_time = startTime;
        request.end_time = endTime;
      } else if (filterType === 'slot_ids') {
        request.slot_ids = selectedSlotIds;
      }

      const response = await api.post('/bulk/export-sets/delete-slots', request);
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slots');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Bulk Delete Slots
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Delete By</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="time_range">Time Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filterType === 'date' && (
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Select value={selectedDate} onValueChange={setSelectedDate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a date" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueDates.map(date => (
                    <SelectItem key={date} value={date}>{date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filterType === 'time_range' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input 
                  placeholder="e.g., 12:00 PM" 
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input 
                  placeholder="e.g., 1:00 PM" 
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
              <p className="col-span-2 text-xs text-slate-500">
                All slots that fall within this time range will be deleted
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Slots
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Copy Attendance Config Dialog
export const CopyAttendanceDialog = ({ open, onOpenChange, exportSetId, teachers, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [sourceTeacher, setSourceTeacher] = useState('');
  const [targetTeachers, setTargetTeachers] = useState([]);

  const handleToggleTarget = (teacherId) => {
    setTargetTeachers(prev => 
      prev.includes(teacherId) 
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const handleSubmit = async () => {
    if (!sourceTeacher) {
      toast.error('Please select a source teacher');
      return;
    }
    if (targetTeachers.length === 0) {
      toast.error('Please select at least one target teacher');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/bulk/export-sets/copy-attendance', {
        export_set_id: exportSetId,
        source_teacher_id: sourceTeacher,
        target_teacher_ids: targetTeachers
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy attendance config');
    } finally {
      setLoading(false);
    }
  };

  const availableTargets = teachers?.filter(t => t.id !== sourceTeacher) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy Attendance Configuration
          </DialogTitle>
          <DialogDescription>
            Copy attendance settings from one teacher to others
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Copy From (Source Teacher)</Label>
            <Select value={sourceTeacher} onValueChange={(v) => {
              setSourceTeacher(v);
              setTargetTeachers(prev => prev.filter(id => id !== v));
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select source teacher" />
              </SelectTrigger>
              <SelectContent>
                {teachers?.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.first_name} {t.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {sourceTeacher && (
            <div className="space-y-2">
              <Label>Copy To (Target Teachers)</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                {availableTargets.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                    <Checkbox 
                      id={`target-${t.id}`}
                      checked={targetTeachers.includes(t.id)}
                      onCheckedChange={() => handleToggleTarget(t.id)}
                    />
                    <Label htmlFor={`target-${t.id}`} className="cursor-pointer flex-1">
                      {t.first_name} {t.last_name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {targetTeachers.length} teacher(s) selected
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !sourceTeacher || targetTeachers.length === 0}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Copy Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Clone Conference Set Dialog
export const CloneConferenceDialog = ({ open, onOpenChange, conferenceSet, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');

  const handleSubmit = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a name for the cloned conference set');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/bulk/conference-sets/clone', {
        source_conference_set_id: conferenceSet.id,
        new_name: newName
      });
      toast.success(response.data.message);
      onSuccess?.(response.data.new_conference_set_id);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone conference set');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Clone Conference Set
          </DialogTitle>
          <DialogDescription>
            Create a copy of "{conferenceSet?.friendly_name}" with all linked schedules
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>New Conference Set Name</Label>
            <Input 
              placeholder="e.g., Spring 2026 Conference"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
            <p className="font-medium mb-1">What will be cloned:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>All teacher selections</li>
              <li>URL display settings</li>
              <li>All linked schedule sets (with "(Clone)" suffix)</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !newName.trim()}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Clone Conference Set
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Add Teachers by Department Dialog
export const AddByDepartmentDialog = ({ open, onOpenChange, conferenceSetId, departments, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const handleToggleDepartment = (deptId) => {
    setSelectedDepartments(prev => 
      prev.includes(deptId) 
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDepartments.length === departments.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departments.map(d => d.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedDepartments.length === 0) {
      toast.error('Please select at least one department');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/bulk/conference-sets/add-by-department', {
        conference_set_id: conferenceSetId,
        department_ids: selectedDepartments
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add teachers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Add Teachers by Department
          </DialogTitle>
          <DialogDescription>
            Add all non-excluded teachers from selected departments
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between">
            <Label>Select Departments</Label>
            <Button variant="ghost" size="sm" onClick={handleSelectAll}>
              {selectedDepartments.length === departments?.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
          <div className="border rounded-lg max-h-64 overflow-y-auto p-2 space-y-1">
            {departments?.map(dept => (
              <div key={dept.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                <Checkbox 
                  id={`dept-${dept.id}`}
                  checked={selectedDepartments.includes(dept.id)}
                  onCheckedChange={() => handleToggleDepartment(dept.id)}
                />
                <Label htmlFor={`dept-${dept.id}`} className="cursor-pointer flex-1">
                  {dept.name}
                </Label>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            {selectedDepartments.length} department(s) selected
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || selectedDepartments.length === 0}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Teachers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Bulk Room Assignment Dialog
export const BulkRoomAssignmentDialog = ({ open, onOpenChange, selectedTeachers, rooms, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');

  const handleSubmit = async () => {
    if (!selectedRoom) {
      toast.error('Please select a room');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/bulk/teachers/assign-room', {
        teacher_ids: selectedTeachers,
        room_id: selectedRoom
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
      setSelectedRoom('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign room');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bulk Room Assignment
          </DialogTitle>
          <DialogDescription>
            Assign a room to {selectedTeachers.length} selected teacher(s)
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label>Select Room</Label>
            <Select value={selectedRoom} onValueChange={setSelectedRoom}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a room..." />
              </SelectTrigger>
              <SelectContent>
                {rooms?.map(room => (
                  <SelectItem key={room.id} value={room.id}>
                    {room.room_name || room.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-slate-500">
            This will assign the selected room to all {selectedTeachers.length} teachers. 
            Any existing room assignments will be replaced.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !selectedRoom}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Assign Room
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Bulk Schedule Slot Toggle Dialog
export const BulkScheduleSlotToggleDialog = ({ open, onOpenChange, scheduleSetId, selectedSlots, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const handleSubmit = async () => {
    if (selectedSlots.length === 0) {
      toast.error('No time slots selected');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/bulk/schedule-sets/toggle-slots', {
        schedule_set_id: scheduleSetId,
        slot_indices: selectedSlots,
        is_active: isActive
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update slots');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Bulk Enable/Disable Time Slots
          </DialogTitle>
          <DialogDescription>
            {selectedSlots.length} time slot(s) selected
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center gap-4">
            <Button
              variant={isActive ? 'default' : 'outline'}
              onClick={() => setIsActive(true)}
              className={isActive ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              Enable Slots
            </Button>
            <Button
              variant={!isActive ? 'default' : 'outline'}
              onClick={() => setIsActive(false)}
              className={!isActive ? 'bg-amber-600 hover:bg-amber-700' : ''}
            >
              Disable Slots
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {isActive 
              ? 'Enabled slots will be included in export files.'
              : 'Disabled slots will be excluded from export files.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isActive ? 'Enable Slots' : 'Disable Slots'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Bulk Remove Teachers from Export Dialog
export const BulkRemoveTeachersFromExportDialog = ({ open, onOpenChange, exportSetId, selectedTeachers, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (selectedTeachers.length === 0) {
      toast.error('No teachers selected');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/bulk/export-sets/remove-teachers', {
        export_set_id: exportSetId,
        teacher_ids: selectedTeachers
      });
      toast.success(response.data.message);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove teachers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <Trash2 className="h-5 w-5" />
            Remove Teachers from Export
          </DialogTitle>
          <DialogDescription>
            This will remove all slots for {selectedTeachers.length} selected teacher(s) from this export set.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
            <p className="text-sm text-rose-800">
              <strong>Warning:</strong> This action will permanently remove all conference slots 
              for the selected teachers from this export set. This cannot be undone.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-rose-600 hover:bg-rose-700"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Remove {selectedTeachers.length} Teacher(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
